import { useMemo } from "react"
import { LicensesSet } from "../../../mp-database"

/**
 * Plain function version for non-React contexts (like getHomePath)
 * @param userLicenses - Array of user licenses
 * @returns Object with ACT and EACT license status flags
 */
export const getLicenseStatus = (userLicenses: LicensesSet[]) => {
  if (!userLicenses || userLicenses.length === 0) {
    return {
      hasOnlyACTLegacyLicenses: false,
      hasACTAndOtherLicenses: false,
      hasOnlyEACTLicenses: false,
      hasEACTAndOtherLicenses: false,
      hasEACTLicense: false
    }
  }

  let hasACTLicense = false
  let hasEACTLicense = false
  let hasOtherLicense = false

  for (const license of userLicenses) {
    // Skip expired licenses
    if (
      license.isExpired ||
      (license.endDate && license.endDate < Date.now())
    ) {
      continue
    }

    const navNameLower = license.navName?.toLowerCase() || ""

    // The first word is always the program name (e.g., "ACT Practice Test" -> "ACT")
    const programName = navNameLower.split(" ")[0]

    // Check if this is an ACT license (but not EACT)
    const isACTOrLegacy =
      programName === "act" || navNameLower.includes("legacy")

    if (isACTOrLegacy) {
      hasACTLicense = true
    } else if (programName === "eact") {
      hasEACTLicense = true
    } else if (programName) {
      // Any other non-empty program name counts as "other"
      hasOtherLicense = true
    }
  }

  const result = {
    // ACT flags (existing)
    hasOnlyACTLegacyLicenses:
      hasACTLicense && !hasEACTLicense && !hasOtherLicense,
    hasACTAndOtherLicenses:
      hasACTLicense && (hasEACTLicense || hasOtherLicense),

    // EACT flags (new)
    hasOnlyEACTLicenses: hasEACTLicense && !hasACTLicense && !hasOtherLicense,
    hasEACTAndOtherLicenses:
      hasEACTLicense && (hasACTLicense || hasOtherLicense),
    hasEACTLicense: hasEACTLicense
  }

  return result
}

/**
 * Hook to determine ACT and EACT license status for a user
 * @param userLicenses - Array of user licenses
 * @returns Object with ACT and EACT license status flags
 */
export const useLicenseStatus = (userLicenses: LicensesSet[]) => {
  return useMemo(() => getLicenseStatus(userLicenses), [userLicenses])
}
